import React from "react";
import PropTypes from "prop-types";
import { Box, LinearProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const SupplyProgressBar = ({
  accumulated,
  threshold,
  progress,
  showLabel = true,
  variant = "default", // "default" | "compact"
}) => {
  const { t } = useTranslation();
  const getProgressColor = (progressValue) => {
    if (progressValue >= 1) return "success"; // 100%+ - green
    if (progressValue >= 0.8) return "warning"; // 80-99% - amber
    return "primary"; // <80% - primary color
  };

  const formatThreshold = (value) => {
    if (value === null || value === undefined) return t("supply.progressBar.notConfigured");
    return value.toString();
  };

  // Don't cap progress for visual representation - show actual progress but handle display properly
  const progressValue = progress * 100;
  const displayValue = Math.min(progressValue, 100); // Cap visual bar at 100%
  const color = getProgressColor(progress);

  // Show if progress exceeds 100%
  const isOverThreshold = progressValue > 100;

  if (variant === "compact") {
    return (
      <MDBox>
        <MDBox display="flex" alignItems="center" gap={1}>
          <LinearProgress
            variant="determinate"
            value={displayValue}
            color={color}
            sx={{
              height: 8,
              borderRadius: 4,
              flexGrow: 1,
              ...(isOverThreshold && {
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "success.main",
                },
              }),
            }}
          />
          {isOverThreshold && (
            <MDTypography variant="caption" color="success" fontWeight="medium">
              +{Math.round(progressValue - 100)}%
            </MDTypography>
          )}
        </MDBox>
        {showLabel && (
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
            <MDTypography variant="caption" color="text">
              {accumulated}/{formatThreshold(threshold)}
            </MDTypography>
            <MDTypography
              variant="caption"
              color={color === "success" ? "success" : "text"}
              fontWeight="medium"
            >
              {Math.round(progressValue)}%
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    );
  }

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="body2" color="text">
          {t("supply.progressBar.progress")}
        </MDTypography>
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDTypography
            variant="body2"
            color={color === "success" ? "success" : "text"}
            fontWeight="medium"
          >
            {Math.round(progressValue)}%
          </MDTypography>
          {isOverThreshold && (
            <MDTypography variant="caption" color="success" fontWeight="medium">
              ({t("supply.progressBar.overThreshold")})
            </MDTypography>
          )}
        </MDBox>
      </MDBox>

      <LinearProgress
        variant="determinate"
        value={displayValue}
        color={color}
        sx={{
          height: 10,
          borderRadius: 5,
          mb: 1,
          "& .MuiLinearProgress-bar": {
            borderRadius: 5,
            ...(isOverThreshold && {
              backgroundColor: "success.main",
            }),
          },
        }}
      />

      {showLabel && (
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="caption" color="text">
            {t("supply.progressBar.accumulated")} {accumulated}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {t("supply.progressBar.threshold")} {formatThreshold(threshold)}
          </MDTypography>
        </MDBox>
      )}
    </MDBox>
  );
};

SupplyProgressBar.propTypes = {
  accumulated: PropTypes.number.isRequired,
  threshold: PropTypes.number,
  progress: PropTypes.number.isRequired,
  showLabel: PropTypes.bool,
  variant: PropTypes.oneOf(["default", "compact"]),
};

export default SupplyProgressBar;
